import { Injectable } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ErrorHandlerService } from '@y42/error-handling';
import { OrgUserQuery } from '@y42/org-user/state';
import { ToastService } from '@y42/toast';
import { UserQuery } from '@y42/user/state';
import { areArraysEqual } from '@y42/util/are-arrays-equal';
import isEqual from 'lodash/isEqual';
import uniqBy from 'lodash/uniqBy';
import {
  catchError,
  distinctUntilChanged,
  filter,
  finalize,
  firstValueFrom,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import {
  ChatEventType,
  ChatMessageCreatedEventData,
  ChatMessageDeletedEventData,
  ChatMessageEditedEventData,
  ChatSessionCreatedEventData,
  ChatSessionEditedEventData,
} from '../models/chat-data-events';
import { ChatRoomHttpModel, ChatRoomMessageHttpModel } from '../models/chat-data-http';
import {
  ChatAvatar,
  ChatRoomCreationStrategy,
  ChatRoomMessageUIModel,
  ChatRoomMessageUIPayload,
  ChatRoomStatus,
  ChatRoomUIModel,
} from '../models/chat-room.model';
import { ChatKeysQuery } from '../state/chat-keys.query';
import { ChatKeysStore } from '../state/chat-keys.store';
import { ChatRoomsQuery } from '../state/chat-rooms.query';
import { ChatRoomsStore } from '../state/chat-rooms.store';
import { ChatHttpService } from './chat-http.service';
import { ChatRealtimeService, ChatWsEvent } from './chat-realtime.service';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class ChatService {
  constructor(
    private readonly chatKeysStore: ChatKeysStore,
    private readonly chatKeysQuery: ChatKeysQuery,
    private readonly chatRoomsStore: ChatRoomsStore,
    private readonly chatRoomsQuery: ChatRoomsQuery,
    private readonly chatHttpService: ChatHttpService,
    private readonly chatRealtimeService: ChatRealtimeService,
    private readonly orgUserQuery: OrgUserQuery,
    private readonly userQuery: UserQuery,
    private readonly errorHandler: ErrorHandlerService,
    private readonly toast: ToastService,
  ) {
    this.initChatRealtimeEventsListener();
    this.initFetchChatRoomsListener();
    this.initFetchChatRoomMessagesListener();
  }

  private readonly chatRoomsRefCount = new Map<string, number>();
  private readonly fetchChatRoomsPusher$ = new Subject<
    | {
        key: string;
        target: string;
        strategy: ChatRoomCreationStrategy;
      }
    | undefined
  >();
  private readonly fetchChatRoomMessagesPusher$ = new Subject<string | undefined>();

  private readonly uiIdToRealIdSubject$ = new Subject<{ uiId: string; id: string }>();
  public readonly uiIdToRealId$ = this.uiIdToRealIdSubject$.pipe(untilDestroyed(this));

  public readonly chatAuthor$: Observable<ChatAvatar> = this.userQuery.activeUser$.pipe(
    distinctUntilChanged(),
    map((activeUser) => ({
      ...activeUser,
      imgSrc: activeUser?.profile_pic,
      isUnknown: false,
    })),
    untilDestroyed(this),
  );

  public readonly mentionUsers$ = this.orgUserQuery.users$.pipe(
    map((users) => users.map((user) => ({ ...user }))),
    untilDestroyed(this),
  );

  getChatRoomsFor(config: { key: string; target: string; strategy: ChatRoomCreationStrategy }) {
    this.increaseRefCountFor(config.key);

    return this.chatKeysQuery
      .select((state) => state?.[config.key])
      .pipe(
        distinctUntilChanged((a, b) => areArraysEqual(a || [], b || [])),
        tap((chatRoomsIds) => {
          if (!chatRoomsIds) {
            this.fetchChatRoomsPusher$.next(config);
          }
        }),
        switchMap((chatRoomsIds) => {
          return this.chatRoomsQuery.selectMany(chatRoomsIds || []).pipe(
            map((chatRooms) => {
              return chatRooms.filter(
                (chatRoom) =>
                  chatRoom.status === 'active' &&
                  chatRoom.target === config.target &&
                  chatRoom.strategy === config.strategy,
              );
            }),
          );
        }),
        finalize(() => this.decreaseRefCountFor(config.key)),
        untilDestroyed(this),
      );
  }

  async createMessageFor(
    chat: {
      id?: string;
      config?: { key: string; target: string; strategy: ChatRoomCreationStrategy };
      metadata?: { uiMultipleChatRoomId?: string; relatedGitEntityPath?: string };
    },
    message: ChatRoomMessageUIPayload,
  ) {
    if (!chat.id && !chat.config) {
      throw new Error('[Chat service] Chat id or config to create a new chat is required');
    }

    let chatRoomId = chat.id;
    if (!chatRoomId) {
      try {
        const strategy = chat.config?.strategy;
        const uiId = chat.metadata?.uiMultipleChatRoomId;
        const relatedGitEntityPath = chat.metadata?.relatedGitEntityPath;

        if (strategy === 'multiple' && !uiId) {
          throw new Error('[Chat service] uiId is required to create a chat room with multiple strategy');
        }

        const metadata = {
          ...(strategy === 'multiple' ? { uiId } : {}),
          ...(relatedGitEntityPath ? { relatedGitEntityPath } : {}),
        };
        const chatRoom = (await firstValueFrom(this.chatHttpService.createOrGetChatRoom(chat.config!, metadata))).data;
        // we rely in all other actions on pusher, but if event won't be received it can lead to inconsistency
        // that's why it's an only case when we update stores with http response (user could create too many chat rooms otherwise)
        this.chatSessionCreated(chatRoom);

        chatRoomId = chatRoom.id;

        if (strategy === 'multiple' && uiId) {
          this.uiIdToRealIdSubject$.next({ uiId, id: chatRoomId });
        }
      } catch (error) {
        this.handleFailedAction({ error, errorMessage: `Could not create a chat room` });
        return;
      }
    }

    try {
      await firstValueFrom(
        this.chatHttpService.createChatRoomMessage(chatRoomId, {
          message: {
            text: message.content,
            html: message.contentHtml,
          },
          mentions: {
            ids: message.mentionUserIds.map((id) => `${id}`),
          },
        }),
      );
    } catch (error) {
      this.handleFailedAction({ error, errorMessage: `Could not send the message`, chatRoomIdToRefresh: chatRoomId });
    }
  }

  async editMessageFor(chatRoomId: string, messageId: string, message: ChatRoomMessageUIPayload) {
    try {
      await firstValueFrom(
        this.chatHttpService.updateChatRoomMessage(chatRoomId, messageId, {
          message: {
            text: message.content,
            html: message.contentHtml,
          },
          mentions: {
            ids: message.mentionUserIds.map((id) => `${id}`),
          },
        }),
      );
    } catch (error) {
      this.handleFailedAction({ error, errorMessage: `Could not edit the message`, chatRoomIdToRefresh: chatRoomId });
    }
  }

  async deleteMessageIn(chatRoomId: string, messageId: string) {
    try {
      await firstValueFrom(this.chatHttpService.deleteChatRoomMessage(chatRoomId, messageId));
    } catch (error) {
      this.handleFailedAction({ error, errorMessage: `Could not delete the message`, chatRoomIdToRefresh: chatRoomId });
    }
  }

  async resolveChatRoom(chatRoomId: string) {
    try {
      const chatRoomData = (await firstValueFrom(this.chatHttpService.markChatRoomAsResolved(chatRoomId))).data;
      this.chatRoomsStore.upsert(chatRoomId, {
        status: chatRoomData.status,
        updatedAt: new Date(chatRoomData.updated_at),
      });
      return true;
    } catch (error) {
      this.handleFailedAction({
        error,
        errorMessage: `Could not resolve the conversation`,
        chatRoomIdToRefresh: chatRoomId,
      });
      return false;
    }
  }

  private initFetchChatRoomsListener() {
    const fetchChatRoomsFor = async (config: {
      key: string;
      target: string;
      strategy: ChatRoomCreationStrategy;
      status?: ChatRoomStatus;
    }) => {
      const chatRooms = await this.chatHttpService.getAllChatRoomsFor(config);
      const activeChatRooms: ChatRoomUIModel[] = chatRooms
        .map((chatRoom) => ({
          id: chatRoom.id,
          createdBy: this.getAvatarFor(chatRoom.created_by),
          createdAt: new Date(chatRoom.created_at),
          updatedAt: new Date(chatRoom.updated_at),
          conversation: [],
          status: chatRoom.status,
          key: config.key,
          target: config.target,
          strategy: config.strategy,
          metadata: chatRoom.metadata,
        }))
        .filter((chatRoom) => chatRoom.status === 'active');

      this.chatKeysStore.update((state) => ({
        ...state,
        [config.key]: activeChatRooms.map((chatRoom) => chatRoom.id),
      }));
      this.chatRoomsStore.upsertMany(activeChatRooms);

      activeChatRooms.forEach((chatRoom) => {
        this.refreshMessagesFor(chatRoom.id);
      });
    };

    this.fetchChatRoomsPusher$
      .pipe(
        distinctUntilChanged((config1, config2) => isEqual(config1, config2)),
        filter((config) => !!config),
        tap((config) => fetchChatRoomsFor({ ...config!, status: 'active' })),
        catchError((error: unknown) => {
          this.handleFailedAction({
            error,
            errorMessage: `Could not fetch conversations`,
          });
          return of(null);
        }),
        untilDestroyed(this),
      )
      .subscribe();
  }

  private initFetchChatRoomMessagesListener() {
    const fetchChatRoomMessagesFor = async (chatRoomId: string) => {
      this.chatRoomsStore.upsert(chatRoomId, {
        loading: true,
      });

      const chatRoomMessages = await this.chatHttpService.getAllMessagesFor(chatRoomId);
      const conversation = chatRoomMessages.map((message) => this.buildUIMessageModel(message));
      this.chatRoomsStore.upsert(chatRoomId, {
        conversation: this.applySortAndUniqueStrategy(conversation),
        loading: false,
      });
    };

    this.fetchChatRoomMessagesPusher$
      .pipe(
        distinctUntilChanged((chatRoomId1, chatRoomId2) => chatRoomId1 === chatRoomId2),
        filter((chatRoomId) => !!chatRoomId),
        tap((chatRoomId) => fetchChatRoomMessagesFor(chatRoomId!)),
        catchError((error: unknown) => {
          this.handleFailedAction({
            error,
            errorMessage: `Could not fetch a single conversation`,
          });
          return of(null);
        }),
        untilDestroyed(this),
      )
      .subscribe();
  }

  private initChatRealtimeEventsListener() {
    const eventResolverMap: Record<ChatEventType, ((data: ChatWsEvent) => void) | undefined> = {
      'chat.session.created': (data: ChatWsEvent) =>
        this.chatSessionCreated((data as ChatSessionCreatedEventData).model),
      'chat.session.updated': (data: ChatWsEvent) => this.chatSessionEdited(data as ChatSessionEditedEventData),
      'chat.message.created': (data: ChatWsEvent) =>
        this.chatMessageCreated((data as ChatMessageCreatedEventData).model),
      'chat.message.updated': (data: ChatWsEvent) => this.chatMessageEdited(data as ChatMessageEditedEventData),
      'chat.message.deleted': (data: ChatWsEvent) => this.chatMessageDeleted(data as ChatMessageDeletedEventData),
    };

    this.chatRealtimeService
      .listen()
      .pipe(
        tap(({ data, event }) => {
          const resolver = eventResolverMap?.[event as ChatEventType];
          resolver?.(data);
        }),
        catchError((error: unknown) => {
          this.handleFailedAction({
            error,
            errorMessage: `Could not resolve conversation event. We recommend to refresh a page.`,
          });
          return of(null);
        }),
        untilDestroyed(this),
      )
      .subscribe();
  }

  private chatSessionCreated(chatSession: ChatRoomHttpModel) {
    const chatRoomsIds = this.chatKeysQuery.getValue()?.[chatSession.key];
    const isInSubscribedKeys = chatRoomsIds !== undefined;
    if (isInSubscribedKeys) {
      if (!chatRoomsIds.includes(chatSession.id)) {
        this.chatKeysStore.update((state) => ({
          ...state,
          [chatSession.key]: [...chatRoomsIds, chatSession.id],
        }));
      }

      this.chatRoomsStore.upsert(chatSession.id, {
        id: chatSession.id,
        createdAt: new Date(chatSession.created_at),
        createdBy: this.getAvatarFor(chatSession.created_by),
        updatedAt: new Date(chatSession.updated_at),
        conversation: [],
        status: chatSession.status,
        key: chatSession.key,
        target: chatSession.target,
        strategy: chatSession.strategy,
        metadata: chatSession.metadata,
      } as ChatRoomUIModel);
    }
  }

  private chatSessionEdited(data: ChatSessionEditedEventData) {
    const chatSession = data.model;
    if (this.chatKeysQuery.getValue()?.[chatSession.key]) {
      this.chatRoomsStore.upsert(chatSession.id, {
        updatedAt: new Date(chatSession.updated_at),
        status: chatSession.status,
      });
    }
  }

  private chatMessageCreated(chatMessage: ChatRoomMessageHttpModel) {
    const chatSessionId = chatMessage.chat_session_id;
    const chatSessionEntity = this.chatRoomsQuery.getEntity(chatSessionId);

    if (chatSessionEntity) {
      if (this.chatKeysQuery.getValue()?.[chatSessionEntity.key]) {
        const conversation = this.applySortAndUniqueStrategy([
          ...chatSessionEntity.conversation,
          this.buildUIMessageModel(chatMessage),
        ]);

        this.chatRoomsStore.upsert(chatSessionId, {
          conversation,
        });
      }
    }
  }

  private chatMessageEdited(data: ChatMessageEditedEventData) {
    const chatMessage = data.model;
    const chatSession = chatMessage.chat_session;

    if (this.chatKeysQuery.getValue()?.[chatSession.key]) {
      const chatSessionEntity = this.chatRoomsQuery.getEntity(chatSession.id);
      if (chatSessionEntity) {
        const conversation = [...chatSessionEntity.conversation];
        const indexToRemove = conversation.findIndex((msg) => chatMessage.id === msg.id);

        if (indexToRemove !== -1) {
          conversation.splice(indexToRemove, 1);

          const conversationWithUpdatedMessage = this.applySortAndUniqueStrategy([
            ...conversation,
            this.buildUIMessageModel(chatMessage),
          ]);
          this.chatRoomsStore.upsert(chatSession.id, {
            conversation: conversationWithUpdatedMessage,
          });
        }
      }
    }
  }

  private chatMessageDeleted(data: ChatMessageDeletedEventData) {
    const chatMessage = data.model;
    const chatSession = chatMessage.chat_session;

    if (this.chatKeysQuery.getValue()?.[chatSession.key]) {
      const chatSessionEntity = this.chatRoomsQuery.getEntity(chatSession.id);
      if (chatSessionEntity) {
        const conversation = [...chatSessionEntity.conversation];
        const indexToRemove = conversation.findIndex((msg) => chatMessage.id === msg.id);

        if (indexToRemove !== -1) {
          conversation.splice(indexToRemove, 1);
          // not need to sort, cause if was sorted with item, will be sorted without it too
          this.chatRoomsStore.upsert(chatSession.id, {
            conversation,
          });
        }
      }
    }
  }

  private buildUIMessageModel(message: {
    id: string;
    chat_session_id: string;
    content: { message: { text: string; html: string } };
    created_at: string;
    updated_at: string;
    created_by: number;
  }): ChatRoomMessageUIModel {
    const isCreatedByActiveUser = message.created_by === this.userQuery.getActiveUserId();
    return {
      id: message.id,
      message: message.content.message.text,
      messageHtml: message.content.message.html,
      messageCreatedTime: new Date(message.created_at),
      messageUpdatedTime: new Date(message.updated_at),
      isAuthorMessage: isCreatedByActiveUser,
      controls: {
        isEditable: isCreatedByActiveUser,
        isDeletable: isCreatedByActiveUser,
      },
      avatar: this.getAvatarFor(message.created_by),
    };
  }

  private getAvatarFor(createdBy: number): ChatAvatar {
    const user =
      createdBy === this.userQuery.getActiveUserId() // we want the freshest "avatar" for logged in user (mainly avatar)
        ? this.userQuery.getActiveUser()
        : this.orgUserQuery.getEntity(createdBy);

    return user?.first_name
      ? {
          full_name: user?.full_name || '',
          imgSrc: user?.profile_pic || null,
          isUnknown: false,
        }
      : {
          full_name: 'Unknown user',
          imgSrc: null,
          isUnknown: true,
        };
  }

  private applySortAndUniqueStrategy(conversation: ChatRoomMessageUIModel[]): ChatRoomMessageUIModel[] {
    conversation.sort((a, b) => a.messageCreatedTime.getTime() - b.messageCreatedTime.getTime());
    return uniqBy(conversation, (msg) => msg.id);
  }

  private increaseRefCountFor(chatRoomKey: string): void {
    const currentNo = this.chatRoomsRefCount.get(chatRoomKey);
    const currentNoPlusOne = (currentNo || 0) + 1;
    this.chatRoomsRefCount.set(chatRoomKey, currentNoPlusOne);
  }

  private decreaseRefCountFor(chatRoomKey: string): void {
    const currentNo = this.chatRoomsRefCount.get(chatRoomKey);
    const currentNoMinusOne = (currentNo || 0) - 1;
    if (currentNoMinusOne < 0) {
      this.handleFailedAction({
        error: new Error(`[CHAT-FEATURE] decreaseRefCountFor tried to decrease a value less than zero.`),
        errorMessage: `The chat is inconsistent. We recommend to refresh a page.`,
      });
      return;
    }

    if (currentNoMinusOne === 0) {
      this.chatRoomsRefCount.delete(chatRoomKey);
    } else {
      this.chatRoomsRefCount.set(chatRoomKey, currentNoMinusOne);
    }

    if (currentNoMinusOne === 0) {
      const chatRoomIds = this.chatKeysQuery.getValue()?.[chatRoomKey];
      this.chatKeysStore.update((state) => {
        delete state[chatRoomKey];
        return state;
      });
      this.chatRoomsStore.remove(chatRoomIds);

      this.fetchChatRoomsPusher$.next(undefined);
      this.fetchChatRoomMessagesPusher$.next(undefined);
    }
  }

  private handleFailedAction(config: { error: unknown; errorMessage: string; chatRoomIdToRefresh?: string }) {
    this.errorHandler.handleError(config.error);
    this.toast.error(config.errorMessage);

    const chatRoomId = config.chatRoomIdToRefresh;
    if (chatRoomId) {
      this.refreshMessagesFor(chatRoomId);
    }
  }

  private refreshMessagesFor(chatRoomId: string) {
    this.fetchChatRoomMessagesPusher$.next(undefined);
    this.fetchChatRoomMessagesPusher$.next(chatRoomId);
  }
}
